/**
 * Client-side token estimation for content budget pre-validation.
 * Mirrors the backend algorithm in select_content_blocks_by_budget.py.
 * The backend remains the authoritative source — this is for instant UI feedback.
 */

/**
 * Supported file extensions matching backend validation.
 */
const SUPPORTED_TEXT_EXTENSIONS = new Set(['.md', '.txt', '.pdf']);
const SUPPORTED_IMAGE_EXTENSIONS = new Set(['.jpg', '.jpeg', '.png', '.gif', '.webp']);

/**
 * Calculate input budget from context_window using the same formula as backend.
 *
 * input_budget = context_window - output_reserve - safety_reserve - overhead - history
 *
 * output_reserve = max(1024, min(8192, 0.20 * context_window))
 * safety_reserve = 0.10 * context_window
 *
 * @param {number} contextWindow - LLM context window in tokens
 * @param {Array} chatMessages - Current chat messages for history token estimation
 * @param {string} [systemPromptText] - The assistant's root prompt text for overhead estimation
 */
export function calculateInputBudget(contextWindow, chatMessages = [], systemPromptText = '') {
  if (!contextWindow || contextWindow <= 0) return 0;

  const outputReserve = Math.max(1024, Math.min(8192, Math.floor(contextWindow * 0.20)));
  const safetyReserve = Math.floor(contextWindow * 0.10);

  // Base overhead for tools, formatting, memory, date/time context, etc.
  const baseOverhead = 500;
  // Estimate system prompt tokens from root prompt text (~4 chars per token)
  const systemPromptTokens = systemPromptText ? Math.ceil(systemPromptText.length / 4) : 0;
  const fixedOverhead = baseOverhead + systemPromptTokens;

  // Estimate history tokens from chat messages (~4 chars per token)
  const historyTokens = estimateChatHistoryTokens(chatMessages);

  return Math.max(0, contextWindow - outputReserve - safetyReserve - fixedOverhead - historyTokens);
}

/**
 * Estimate tokens consumed by chat history messages.
 * Uses ~4 chars per token approximation (tiktoken cl100k_base).
 */
function estimateChatHistoryTokens(messages) {
  if (!messages || messages.length === 0) return 0;
  let totalChars = 0;
  for (const msg of messages) {
    // Each message has role overhead (~4 tokens)
    totalChars += 16;
    if (typeof msg.content === 'string') {
      totalChars += msg.content.length;
    } else if (Array.isArray(msg.contentBlocks)) {
      for (const block of msg.contentBlocks) {
        const text = block?.data?.text || '';
        totalChars += text.length;
      }
    }
  }
  return Math.ceil(totalChars / 4);
}

/**
 * Extract file extension from a filename.
 */
function getFileExtension(filename) {
  if (!filename) return '';
  const lastDot = filename.lastIndexOf('.');
  return lastDot >= 0 ? filename.substring(lastDot).toLowerCase() : '';
}

/**
 * Check if a file is supported by the backend budget system.
 */
function isSupportedFileType(filename, mimeType) {
  const ext = getFileExtension(filename);
  if (SUPPORTED_TEXT_EXTENSIONS.has(ext) || SUPPORTED_IMAGE_EXTENSIONS.has(ext)) return true;
  // Also accept by mime type for images without extension
  if (mimeType?.startsWith('image/')) return true;
  return false;
}

/**
 * Estimate token count for a single attachment based on file size and type.
 *
 * The backend counts tokens on the base64-encoded content string (not decoded text).
 * Base64 increases size by ~33%, and tiktoken processes the base64 at ~0.66 tokens/char.
 * Net effect: ~0.9 tokens per raw byte. We use this ratio for all file types.
 */
function estimateAttachmentTokens(attachment) {
  const { size, mimeType, name } = attachment;
  if (!size || size <= 0) return 0;

  const ext = getFileExtension(name);

  // Images: fixed cost matching backend (min 1200 tokens)
  if (SUPPORTED_IMAGE_EXTENSIONS.has(ext) || mimeType?.startsWith('image/')) return 1200;

  // Text and PDF files: backend counts tokens on the base64-encoded string.
  // Empirical ratio: ~0.9 tokens per raw byte (base64 expansion * tiktoken rate).
  if (ext === '.txt' || ext === '.md' || ext === '.pdf') {
    return Math.ceil(size * 0.9);
  }

  // Fallback (shouldn't reach here if isSupportedFileType is checked first)
  return Math.ceil(size * 0.9);
}

/**
 * Estimate content budget for a list of attachments.
 * Runs the same greedy accept/reject algorithm as the backend.
 *
 * @param {Array} attachments - Array of {name, size, mimeType, contentBlock, ...}
 * @param {number|null} contextWindow - LLM context window in tokens (from assistant detail)
 * @param {Array} chatMessages - Current chat messages for history token estimation
 * @param {string} [systemPromptText] - The assistant's root prompt text for overhead estimation
 * @returns {Object|null} contentBudget-compatible object, or null if all fit / no context_window
 */
export function estimateBudgetForAttachments(attachments, contextWindow, chatMessages = [], systemPromptText = '') {
  if (!attachments || attachments.length === 0) return null;
  if (!contextWindow || contextWindow <= 0) return null;

  const inputBudget = calculateInputBudget(contextWindow, chatMessages, systemPromptText);
  if (inputBudget <= 0) return null;

  // Only estimate non-oversized attachments that have content
  const estimable = attachments.filter(att => !att.oversized && att.contentBlock);
  if (estimable.length === 0) return null;

  let usedTokens = 0;
  const accepted = [];
  const rejected = [];

  for (const att of estimable) {
    // Check supported file type (matching backend validation)
    if (!isSupportedFileType(att.name, att.mimeType)) {
      rejected.push({
        filename: att.name,
        type: att.contentBlock?.type || 'file',
        estimated_tokens: 0,
        reason: 'unsupported_file_type',
      });
      continue;
    }

    const tokens = estimateAttachmentTokens(att);

    if (usedTokens + tokens <= inputBudget) {
      usedTokens += tokens;
      accepted.push({ filename: att.name, estimated_tokens: tokens });
    } else {
      rejected.push({
        filename: att.name,
        type: att.contentBlock?.type || 'file',
        estimated_tokens: tokens,
        reason: 'input_budget_exceeded',
      });
    }
  }

  if (rejected.length === 0) return null;

  return {
    client_estimate: true,
    context_window: contextWindow,
    input_budget: inputBudget,
    input_used: usedTokens,
    total_rejected_tokens: rejected.reduce((s, r) => s + r.estimated_tokens, 0),
    rejected_blocks: rejected,
    accepted_blocks_count: accepted.length,
    rejected_blocks_count: rejected.length,
  };
}

export { estimateAttachmentTokens, isSupportedFileType };
